import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { Phone, Mail, StickyNote, Plus, Trash2, ChevronDown, ChevronRight, Download, X, Flame, Snowflake, Sun } from 'lucide-react';

const DEFAULT_STAGES = [
  { key: 'new', label: 'New Lead' },
  { key: 'attempting_contact', label: 'Attempting Contact' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'nurturing', label: 'Nurturing' },
  { key: 'qualified', label: 'Qualified' },
  { key: 'converted', label: 'Converted' },
  { key: 'lost', label: 'Lost' },
];

const tierOf = (score) => (score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'cold');
const TierIcon = { hot: Flame, warm: Sun, cold: Snowflake };

const ScoreBadge = ({ score }) => {
  const tier = tierOf(score);
  const Icon = TierIcon[tier];
  return (
    <span className={`lead-score-badge ${tier}`} data-testid="lead-score-badge" title={`Lead score: ${score}/100`}>
      <Icon size={12} /> {score}
    </span>
  );
};

const emptyForm = { lob_id: '', first: '', last: '', email: '', phone: '', purpose: '', amount: '', stage: 'new', notes: '' };

const Leads = ({ lob }) => {
  const { lobs } = useData();
  const darkMode = useDarkMode();
  const [lobFilter, setLobFilter] = useState(lob?.id || 'all');
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [selected, setSelected] = useState([]);
  const [bulkStage, setBulkStage] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, lob_id: lob?.id || '' });
  const [noteLead, setNoteLead] = useState(null);
  const [noteText, setNoteText] = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr('');
    api.get('/leads', { params: { lob_id: lobFilter, q: search || undefined } })
      .then(({ data }) => setLeads(data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [lobFilter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected([]); }, [lobFilter]);

  const stages = useMemo(() => {
    if (lobFilter === 'all') return DEFAULT_STAGES;
    const l = lobs.find((x) => x.id === lobFilter);
    return (l?.leadStages?.length ? l.leadStages : DEFAULT_STAGES);
  }, [lobFilter, lobs]);

  const grouped = useMemo(() => {
    const byKey = {};
    stages.forEach((s) => { byKey[s.key] = []; });
    leads.forEach((l) => {
      const k = byKey[l.stage] ? l.stage : (stages[0]?.key || 'new');
      if (!byKey[k]) byKey[k] = [];
      byKey[k].push(l);
    });
    Object.keys(byKey).forEach((k) => byKey[k].sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0)));
    return byKey;
  }, [leads, stages]);

  const patchLead = (id, updates) => {
    api.patch(`/leads/${id}`, updates).then(load).catch((e) => setErr(formatApiError(e)));
  };

  const deleteLead = (id) => {
    if (!window.confirm('Delete this lead?')) return;
    api.delete(`/leads/${id}`).then(load).catch((e) => setErr(formatApiError(e)));
  };

  const toggleSelected = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const applyBulkStage = () => {
    if (!bulkStage || selected.length === 0) return;
    api.post('/leads/bulk', { ids: selected, action: 'stage', value: bulkStage })
      .then(() => { setSelected([]); setBulkStage(''); load(); })
      .catch((e) => setErr(formatApiError(e)));
  };

  const bulkDelete = () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} selected lead(s)?`)) return;
    api.post('/leads/bulk', { ids: selected, action: 'delete' })
      .then(() => { setSelected([]); load(); })
      .catch((e) => setErr(formatApiError(e)));
  };

  const saveNote = () => {
    if (!noteLead) return;
    const stamp = new Date().toLocaleString();
    const merged = `${noteLead.notes ? noteLead.notes + '\n\n' : ''}[${stamp}] ${noteText}`.trim();
    patchLead(noteLead.id, { notes: merged });
    setNoteLead(null); setNoteText('');
  };

  const submitCreate = (e) => {
    e.preventDefault();
    if (!form.lob_id || !form.first) return;
    const payload = { ...form, amount: form.amount ? Number(form.amount) : undefined };
    api.post('/leads', payload)
      .then(() => { setShowCreate(false); setForm({ ...emptyForm, lob_id: lob?.id || '' }); load(); })
      .catch((e) => setErr(formatApiError(e)));
  };

  const exportCsv = () => {
    const rows = [['Name', 'Email', 'Phone', 'Purpose', 'Stage', 'Score', 'Business Line']];
    leads.forEach((l) => rows.push([
      `${l.first || ''} ${l.last || ''}`.trim(), l.email || '', l.phone || '', l.purpose || '',
      l.stage || '', l.lead_score ?? '', l.lob_label || '',
    ]));
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'leads.csv';
    a.click();
  };

  return (
    <div className="module-pane" data-testid="leads-module">
      <div className="page-toolbar" style={{ flexWrap: 'wrap', gap: 10 }}>
        <div className="lead-lob-tabs" data-testid="lead-lob-tabs">
          <button
            className={`lead-lob-tab ${lobFilter === 'all' ? 'active' : ''}`}
            onClick={() => setLobFilter('all')}
            data-testid="lead-lob-tab-all"
          >All Business Lines</button>
          {lobs.map((l) => (
            <button
              key={l.id}
              className={`lead-lob-tab ${lobFilter === l.id ? 'active' : ''}`}
              style={lobFilter === l.id ? { background: l.accent, borderColor: l.accent, color: '#fff' } : {}}
              onClick={() => setLobFilter(l.id)}
              data-testid={`lead-lob-tab-${l.id}`}
            >{l.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
          <input
            className="list-search" placeholder="Search leads…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="lead-search-input"
          />
          <button className="dp-btn" onClick={exportCsv} data-testid="export-leads-btn"><Download size={14} /> Export</button>
          <button
            className="dp-btn primary"
            onClick={() => { setForm({ ...emptyForm, lob_id: lobFilter !== 'all' ? lobFilter : (lobs[0]?.id || '') }); setShowCreate(true); }}
            data-testid="add-lead-btn"
          ><Plus size={14} /> Add Lead</button>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="bulk-bar" data-testid="bulk-actions-bar">
          <span>{selected.length} selected</span>
          <select value={bulkStage} onChange={(e) => setBulkStage(e.target.value)} className="sel-input" data-testid="bulk-stage-select">
            <option value="">Move to stage…</option>
            {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button className="dp-btn primary" onClick={applyBulkStage} data-testid="bulk-apply-btn">Apply</button>
          <button className="dp-btn danger" onClick={bulkDelete} data-testid="bulk-delete-btn"><Trash2 size={13} /> Delete</button>
          <button className="dp-btn" onClick={() => setSelected([])} data-testid="bulk-clear-btn">Clear</button>
        </div>
      )}

      {err && <div className="module-banner err" data-testid="leads-error">{err}</div>}

      <div className="leads-scroll">
        {loading ? (
          <div className="empty-state" data-testid="leads-loading"><div className="ttl">Loading leads…</div></div>
        ) : leads.length === 0 ? (
          <div className="empty-state" data-testid="leads-empty"><div className="ttl">No leads yet</div><div className="sub">Add your first lead to start building your queue.</div></div>
        ) : (
          stages.map((s) => {
            const rows = grouped[s.key] || [];
            if (rows.length === 0) return null;
            const isCollapsed = !!collapsed[s.key];
            return (
              <div className="stage-section" key={s.key} data-testid={`stage-section-${s.key}`}>
                <div
                  className="stage-header"
                  onClick={() => setCollapsed((c) => ({ ...c, [s.key]: !c[s.key] }))}
                  data-testid={`stage-toggle-${s.key}`}
                >
                  {isCollapsed ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                  <span className="stage-dot" style={{ background: lob?.accent || '#0a1020' }} />
                  <b>{s.label}</b> <span className="count">({rows.length})</span>
                </div>
                {!isCollapsed && (
                  <table className="tbl leads-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 30 }}></th>
                        <th style={{ width: 108 }}></th>
                        <th>Score</th>
                        <th>Status</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Purpose</th>
                        {lobFilter === 'all' && <th>Business Line</th>}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((l) => (
                        <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                          <td>
                            <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggleSelected(l.id)} data-testid={`lead-checkbox-${l.id}`} />
                          </td>
                          <td>
                            <div className="row-actions">
                              <a href={l.phone ? `tel:${l.phone}` : undefined} className={`icon-btn ${!l.phone ? 'disabled' : ''}`} data-testid={`lead-call-${l.id}`} title="Call"><Phone size={13} /></a>
                              <a href={l.email ? `mailto:${l.email}` : undefined} className={`icon-btn ${!l.email ? 'disabled' : ''}`} data-testid={`lead-email-${l.id}`} title="Email"><Mail size={13} /></a>
                              <button className="icon-btn" onClick={() => { setNoteLead(l); setNoteText(''); }} data-testid={`lead-note-${l.id}`} title="Add note"><StickyNote size={13} /></button>
                            </div>
                          </td>
                          <td><ScoreBadge score={l.lead_score || 0} /></td>
                          <td>
                            <select
                              className="sel-input stage-select" value={l.stage || s.key}
                              onChange={(e) => patchLead(l.id, { stage: e.target.value })}
                              data-testid={`lead-stage-select-${l.id}`}
                            >
                              {stages.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
                            </select>
                          </td>
                          <td><b>{l.first} {l.last}</b></td>
                          <td>{l.email || <span className="muted">—</span>}</td>
                          <td>{l.phone || <span className="muted">—</span>}</td>
                          <td>{l.purpose || <span className="muted">—</span>}</td>
                          {lobFilter === 'all' && <td>{l.lob_label}</td>}
                          <td>
                            <button className="icon-btn danger" onClick={() => deleteLead(l.id)} data-testid={`lead-delete-${l.id}`} title="Delete"><Trash2 size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })
        )}
      </div>

      {showCreate && (
        <div className="modal-overlay-v2" onClick={() => setShowCreate(false)}>
          <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submitCreate} data-testid="lead-modal">
            <div className="modal-head"><b>Add Lead</b><button type="button" className="icon-btn" onClick={() => setShowCreate(false)}><X size={16} /></button></div>
            <label className="f-label">Business Line</label>
            <select className="f-select" value={form.lob_id} onChange={(e) => setForm({ ...form, lob_id: e.target.value })} required data-testid="lead-modal-lob-select">
              <option value="">Select…</option>
              {lobs.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
            <div className="field-row">
              <div><label className="f-label">First Name</label><input className="f-input" value={form.first} onChange={(e) => setForm({ ...form, first: e.target.value })} required data-testid="lead-modal-first-input" /></div>
              <div><label className="f-label">Last Name</label><input className="f-input" value={form.last} onChange={(e) => setForm({ ...form, last: e.target.value })} data-testid="lead-modal-last-input" /></div>
            </div>
            <div className="field-row">
              <div><label className="f-label">Email</label><input className="f-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-modal-email-input" /></div>
              <div><label className="f-label">Phone</label><input className="f-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-modal-phone-input" /></div>
            </div>
            <div className="field-row">
              <div><label className="f-label">Purpose</label><input className="f-input" placeholder="e.g. Fix & Flip, Refinance" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="lead-modal-purpose-input" /></div>
              <div><label className="f-label">Loan Amount</label><input className="f-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="lead-modal-amount-input" /></div>
            </div>
            <label className="f-label">Stage</label>
            <select className="f-select" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="lead-modal-stage-select">
              {(lobs.find((x) => x.id === form.lob_id)?.leadStages?.length ? lobs.find((x) => x.id === form.lob_id).leadStages : DEFAULT_STAGES).map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
            <label className="f-label">Notes</label>
            <textarea className="f-textarea" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-modal-notes-input" />
            <div className="modal-actions">
              <button type="button" className="dp-btn" onClick={() => setShowCreate(false)}>Cancel</button>
              <button type="submit" className="dp-btn primary" data-testid="lead-modal-save-btn">Save Lead</button>
            </div>
          </form>
        </div>
      )}

      {noteLead && (
        <div className="modal-overlay-v2" onClick={() => setNoteLead(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="note-modal">
            <div className="modal-head"><b>Note for {noteLead.first} {noteLead.last}</b><button className="icon-btn" onClick={() => setNoteLead(null)}><X size={16} /></button></div>
            <textarea className="f-textarea" rows={4} autoFocus placeholder="What happened on this call?" value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="note-modal-input" />
            <div className="modal-actions">
              <button className="dp-btn" onClick={() => setNoteLead(null)}>Cancel</button>
              <button className="dp-btn primary" onClick={saveNote} disabled={!noteText.trim()} data-testid="note-modal-save-btn">Save Note</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;
